import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/landing/Logo";
import { loginStep1, loginVerifyOtp, loginResendOtp } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Lock, Mail, Loader2 } from "lucide-react";
import { OtpModal } from "@/components/auth/OtpModal";

function formatErr(detail) {
  if (!detail) return "Login failed";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [step1Error, setStep1Error] = useState("");

  // OTP step state
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpToken, setOtpToken] = useState(null);
  const [mobileMasked, setMobileMasked] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStep1Error("");
    try {
      const r = await loginStep1(email, pw);
      if (r?.requires_otp && r?.otp_token) {
        setOtpToken(r.otp_token);
        setMobileMasked(r.mobile_masked || "");
        setOtpOpen(true);
        toast.success("OTP sent to your WhatsApp");
      } else {
        setStep1Error("Unexpected response from server");
      }
    } catch (err) {
      const msg = formatErr(err?.response?.data?.detail) || "Login failed";
      setStep1Error(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const onVerify = async (code) => {
    const u = await loginVerifyOtp(otpToken, code);
    setUser(u);
    toast.success(`Welcome back, ${u.name || "Admin"}`);
    const to = loc.state?.from?.pathname || "/admin";
    setTimeout(() => nav(to, { replace: true }), 400);
  };

  const onResend = async () => {
    await loginResendOtp(otpToken);
    toast.success("New OTP sent");
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-slate-50 px-6 relative overflow-hidden" data-testid="admin-login-page">
      <div className="absolute -top-32 -left-20 h-96 w-96 rounded-full bg-blue-200/40 blur-3xl" />
      <div className="absolute -bottom-32 -right-20 h-96 w-96 rounded-full bg-emerald-200/40 blur-3xl" />

      <div className="relative w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Logo />
        </div>
        <div className="rounded-3xl bg-white border border-slate-200 shadow-xl p-8">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
            Admin sign in
          </h1>
          <p className="mt-1 text-sm text-slate-600">Two-step verification keeps your dashboard secure.</p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="admin-login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email"
                  className="pl-9"
                  placeholder="admin@wappit.com"
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/admin/forgot-password" className="text-xs font-semibold text-emerald-700 hover:text-emerald-800" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  required
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  data-testid="login-password"
                  className="pl-9"
                  placeholder="••••••••"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full rounded-full h-11 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}
            </Button>
            {step1Error && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] font-semibold text-rose-700" data-testid="login-step1-error">
                {step1Error}
              </div>
            )}
          </form>

          <p className="mt-6 text-xs text-slate-400 text-center">
            Authorized personnel only. We send a 6-digit code to your WhatsApp on every login.
          </p>
        </div>
      </div>

      <OtpModal
        open={otpOpen}
        onClose={() => setOtpOpen(false)}
        mobileMasked={mobileMasked.replace(/^\*+/, "")}
        title="Verify it's you"
        description={<>We&apos;ve sent a 6-digit code to your WhatsApp ending in <b>{mobileMasked.slice(-4)}</b></>}
        onVerify={onVerify}
        onResend={onResend}
        onChangeNumber={() => setOtpOpen(false)}
        testIdPrefix="admin-login-otp"
      />
    </main>
  );
}
